import type { ProvenanceDocumentKind } from "@ondo/shared";
import { useRef } from "react";

import { DocumentVerdict } from "./DocumentVerdict.tsx";
import {
  ACCEPTED_DOCUMENT_TYPES,
  DOCUMENT_KINDS,
  PROVENANCE_COPY,
  PROVENANCE_LABELS,
} from "./provenance.constants.ts";
import type { DocumentRow } from "./provenance.model.ts";

/** The evidence attached to one link. */
export function ProvenanceDocuments({
  rows,
  refusals,
  editable,
  onAdd,
  onSetKind,
  onOpen,
  onRemove,
  onJudge,
  onUndoVerdict,
}: {
  rows: DocumentRow[];
  refusals: string[];
  editable: boolean;
  onAdd: (files: File[]) => void;
  onSetKind: (documentId: string, kind: ProvenanceDocumentKind) => void;
  onOpen: (documentId: string) => void;
  onRemove: (documentId: string) => void;
  /**
   * Accept or reject one paper — OND-62.
   *
   * Returns the problem sentence or null, which is `ProvenanceActComposer`'s
   * contract: a rejection with no reason is refused by the model before the
   * server refuses it, and the field stays open with the words still in it.
   */
  onJudge: (documentId: string, stance: "verify" | "dispute", reason: string) => string | null;
  /** Ends the reader's own verdict, by its id. */
  onUndoVerdict: (verdictId: string) => void;
}) {
  const picker = useRef<HTMLInputElement>(null);

  return (
    <section className="provenance__subcard">
      <div className="provenance__subcard-head">
        <h4 className="provenance__subcard-title">{PROVENANCE_LABELS.documents}</h4>
        {editable && (
          <>
            <input
              ref={picker}
              type="file"
              multiple
              accept={ACCEPTED_DOCUMENT_TYPES.join(",")}
              onChange={(event) => {
                onAdd([...(event.target.files ?? [])]);
                // Cleared so re-picking the same file after removing it still fires.
                event.target.value = "";
              }}
            />
            <button
              type="button"
              className="provenance__subcard-action"
              onClick={() => picker.current?.click()}
            >
              {PROVENANCE_COPY.upload}
            </button>
          </>
        )}
      </div>

      {refusals.length > 0 && (
        <ul className="provenance__refusals" role="alert">
          {refusals.map((refusal) => (
            <li key={refusal}>{refusal}</li>
          ))}
        </ul>
      )}

      {rows.length === 0 ? (
        <p className="provenance__empty">{PROVENANCE_COPY.documentsEmpty}</p>
      ) : (
        <ul className="provenance__docs">
          {rows.map((row) => (
            <li key={row.id} className="provenance__doc">
              <span className="provenance__doc-mark" aria-hidden="true">
                📄
              </span>
              {/* A button, not an anchor: nothing is signed until somebody asks
                  to look, so there is no href to put here and an anchor without
                  one is a lie about what the control does. */}
              <button
                type="button"
                className="provenance__doc-name"
                aria-label={row.openLabel}
                onClick={() => onOpen(row.id)}
              >
                {row.name}
              </button>
              {editable ? (
                <select
                  aria-label={`${PROVENANCE_LABELS.documentKind}: ${row.name}`}
                  value={row.kind}
                  onChange={(event) =>
                    onSetKind(row.id, event.target.value as ProvenanceDocumentKind)
                  }
                >
                  {DOCUMENT_KINDS.map((kind) => (
                    <option key={kind.key} value={kind.key}>
                      {kind.label}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="provenance__doc-type">{row.kindLabel}</span>
              )}
              <span className="provenance__doc-size">{row.sizeLabel}</span>
              {row.removable && (
                <button
                  type="button"
                  className="provenance__doc-remove"
                  aria-label={`Remove ${row.name}`}
                  onClick={() => onRemove(row.id)}
                >
                  ✕
                </button>
              )}
              {/* Null on a draft's file, which has no row to hang a verdict off
                  — the model decided that, not this list. */}
              {row.verdict !== null && (
                <DocumentVerdict
                  verdict={row.verdict}
                  name={row.name}
                  onAccept={() => onJudge(row.id, "verify", "")}
                  onReject={(reason) => onJudge(row.id, "dispute", reason)}
                  onUndo={() => {
                    const mineId = row.verdict?.mineId;
                    if (mineId !== null && mineId !== undefined) onUndoVerdict(mineId);
                  }}
                />
              )}
            </li>
          ))}
        </ul>
      )}

      {editable && <p className="provenance__help">{PROVENANCE_COPY.documentsHelp}</p>}
    </section>
  );
}
